import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { BUSINESS } from "../lib/constants";
import { Toaster } from "../components/ui/sonner";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user.id) nav("/admin", { replace: true });
  }, [user, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back!");
      nav("/admin", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy text-white flex flex-col">
      <div className="px-4 sm:px-8 py-6 flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-display uppercase text-sm tracking-wider text-white/70 hover:text-gold"
        >
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>
        <img
          src={BUSINESS.logo}
          alt="B&C"
          className="h-10 w-10 object-contain"
        />
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="w-full max-w-md bg-white text-navy border-2 border-gold p-8 sm:p-10"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 flex items-center justify-center bg-gold text-navy border-2 border-navy">
              <Lock className="h-5 w-5" strokeWidth={2.5} />
            </div>
            <div>
              <p className="font-body text-xs uppercase tracking-[0.25em] text-navy/60">
                Admin Access
              </p>
              <h1 className="font-display uppercase text-2xl leading-none mt-1">
                Sign In
              </h1>
            </div>
          </div>

          <label className="block mb-4">
            <span className="block font-body text-xs font-bold uppercase tracking-[0.18em] text-navy mb-2">
              Email
            </span>
            <input
              data-testid="admin-email-input"
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-white border-2 border-navy outline-none font-body focus:shadow-[4px_4px_0_0_#f5a623] focus:-translate-x-0.5 focus:-translate-y-0.5 transition-transform"
              placeholder="you@email.com"
            />
          </label>

          <label className="block mb-6">
            <span className="block font-body text-xs font-bold uppercase tracking-[0.18em] text-navy mb-2">
              Password
            </span>
            <input
              data-testid="admin-password-input"
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-white border-2 border-navy outline-none font-body focus:shadow-[4px_4px_0_0_#f5a623] focus:-translate-x-0.5 focus:-translate-y-0.5 transition-transform"
              placeholder="••••••••"
            />
          </label>

          <button
            data-testid="admin-login-btn"
            disabled={loading}
            type="submit"
            className="brutal-btn w-full inline-flex items-center justify-center gap-2 bg-navy text-white px-6 py-4 font-display text-sm uppercase tracking-wider border-2 border-navy disabled:opacity-70"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
              </>
            ) : (
              "Sign In"
            )}
          </button>

          <p className="mt-5 text-xs text-navy/60 text-center">
            Authorized personnel only.
          </p>
        </form>
      </div>
      <Toaster position="top-center" />
    </div>
  );
}
